import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { aggregate, defaultWeightsFrom, scoreColor, CATEGORY_ORDER, gateState } from './companyScore';
import * as presetStore from './companyScorePresetStore';
import { getCompanyScoreAssignments, saveCompanyScoreAssignment } from '../../../services/api';
import CompanyScoreMethodologyModal from './CompanyScoreMethodologyModal';
import CompanyScoreMethodEditorModal from './CompanyScoreMethodEditorModal';

const DEFAULT_METHOD_ID = 'default';

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

function polar(cx, cy, r, angleDeg) {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}
function arcPath(cx, cy, r, startAngle, endAngle) {
  const start = polar(cx, cy, r, endAngle);
  const end = polar(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

function Gauge({ value }) {
  const cx = 90;
  const cy = 84;
  const r = 72;
  const f = value == null ? 0 : Math.max(0, Math.min(100, value)) / 100;
  const color = scoreColor(value);
  return (
    <svg width="180" height="100" viewBox="0 0 180 100" role="img" aria-label="company score gauge">
      <path d={arcPath(cx, cy, r, -90, 90)} stroke="#374151" strokeWidth="12" fill="none" strokeLinecap="round" />
      {f > 0 && (
        <path
          d={arcPath(cx, cy, r, -90, -90 + 180 * f)}
          stroke={color}
          strokeWidth="12"
          fill="none"
          strokeLinecap="round"
        />
      )}
      <text x={cx} y={cy - 8} textAnchor="middle" fontSize="32" fontWeight="700" fill={color}>
        {value == null ? '—' : Math.round(value)}
      </text>
      <text x={cx} y={cy + 10} textAnchor="middle" fontSize="10" fill="#9ca3af">
        out of 100
      </text>
    </svg>
  );
}

// The fair-value scenario signal keys (their value = $target + % upside).
const FV_KEYS = new Set([
  'fv_avg_upside', 'fv_low_upside', 'fv_high_upside',
  'fv_avg_next_upside', 'fv_low_next_upside', 'fv_high_next_upside',
]);

// Format a signal's actual scored value for the detail breakdown, using the
// unit hint from methodology. Fair-value rows show "$<target> (<+pct%>)".
function fmtSignalValue(sig, meta, fairValues) {
  const { key, raw } = sig;

  if (FV_KEYS.has(key)) {
    const target = fairValues?.[key]?.target;
    const pctStr = raw == null ? null : `${raw >= 0 ? '+' : ''}${Math.round(raw)}%`;
    if (target != null) return `$${Math.round(target)}${pctStr ? ` (${pctStr})` : ''}`;
    return pctStr || '—';
  }

  if (raw == null) return '—';
  const unit = meta?.[key]?.unit || '';
  if (unit === 'x') return `${raw.toFixed(1)}x`;
  if (unit === 'ratio') return raw.toFixed(2);
  if (unit === 'index') return `${Math.round(raw)}`;
  if (unit.startsWith('%')) return `${raw.toFixed(1)}%`;
  return `${raw}`;
}

// Multi-line hover tooltip for a fair-value row, mirroring the Fair Value tab's
// "FV = P/E × EPS" model. Returns undefined for non-fair-value signals.
function fvTooltip(sig, fairValues, price) {
  if (!FV_KEYS.has(sig.key)) return undefined;
  const fv = fairValues?.[sig.key];
  if (!fv || fv.pe == null || fv.eps == null || fv.target == null) return undefined;
  const upside =
    fv.upside == null ? '' : ` → ${fv.upside >= 0 ? '+' : ''}${fv.upside.toFixed(1)}%`;
  const vsPrice = price != null ? `vs $${Number(price).toFixed(2)} price` : '';
  const epsLabel = fv.eps_label || 'EPS';
  return (
    `${fv.pe_label} Fair Value\n` +
    `$${fv.eps.toFixed(2)} EPS (${epsLabel}) × ${fv.pe.toFixed(1)} (${fv.pe_label}) = $${Math.round(fv.target)}\n` +
    `${vsPrice}${upside}`
  );
}

// Per-category detail breakdown for the selected stock. Shows ONLY the features
// included in that stock's method (weight > 0), so it reflects just what's
// relevant to the method scoring it.
function CategoryBreakdown({ result, meta, fairValues, price }) {
  if (!result?.categories?.length) return null;
  const signalsByKey = Object.fromEntries((result.signals || []).map((s) => [s.key, s]));
  return (
    <div className="mt-3 space-y-2">
      {result.categories.map((cat) => {
        const visible = cat.signals.filter((k) => (signalsByKey[k]?.weight || 0) > 0);
        if (!visible.length) return null;
        return (
          <div key={cat.name} className="border border-gray-800 rounded-md p-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-semibold text-gray-300">{cat.name}</span>
              <span className="text-xs font-bold" style={{ color: scoreColor(cat.subscore) }}>
                {cat.subscore == null ? '—' : Math.round(cat.subscore)}
              </span>
            </div>
            <div className="space-y-0.5">
              {visible.map((key) => {
                const s = signalsByKey[key];
                if (!s) return null;
                return (
                  <div key={key} className="flex justify-between items-center text-[11px] text-gray-400">
                    <span className={s.subscore == null ? 'italic text-gray-600' : ''}>{s.label}</span>
                    <span className="flex items-center gap-3">
                      <span
                        className={`text-gray-500 tabular-nums ${FV_KEYS.has(s.key) ? 'cursor-help underline decoration-dotted decoration-gray-600' : ''}`}
                        title={fvTooltip(s, fairValues, price)}
                      >
                        {fmtSignalValue(s, meta, fairValues)}
                      </span>
                      {s.subscore == null ? (
                        <span className="text-gray-600 w-6 text-right">—</span>
                      ) : (
                        <span className="w-6 text-right font-medium" style={{ color: scoreColor(s.subscore) }}>
                          {Math.round(s.subscore)}
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ranked table (with per-row Method selector)
// ---------------------------------------------------------------------------

function RankedTable({ rows, methods, selectedTicker, onSelect, onAssign }) {
  return (
    <div className="overflow-x-auto border border-gray-800 rounded-lg">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-800 bg-gray-900/60">
            <th className="py-2 px-3 text-left font-medium">#</th>
            <th className="py-2 px-3 text-left font-medium">Ticker</th>
            <th className="py-2 px-2 text-left font-medium">Status</th>
            <th className="py-2 px-3 text-left font-medium">Method</th>
            <th className="py-2 px-3 text-right font-medium">Score</th>
            {CATEGORY_ORDER.map((c) => (
              <th key={c} className="py-2 px-3 text-right font-medium whitespace-nowrap">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const catMap = Object.fromEntries((row.result.categories || []).map((c) => [c.name, c.subscore]));
            const isSel = row.ticker === selectedTicker;
            return (
              <tr
                key={row.ticker}
                onClick={() => onSelect(row.ticker)}
                className={`border-b border-gray-800/60 cursor-pointer transition-colors ${
                  isSel ? 'bg-blue-900/30' : 'hover:bg-gray-800/50'
                }`}
              >
                <td className="py-2 px-3 text-gray-500">{i + 1}</td>
                <td className={`py-2 px-3 font-semibold ${row.gate?.eligible === false ? 'text-gray-400' : 'text-gray-100'}`}>{row.ticker}</td>
                <td className="py-2 px-2">
                  {row.gate && (row.gate.eligible === false || row.gate.flags?.length > 0) ? (
                    <span
                      className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded cursor-help ${
                        row.gate.eligible === false
                          ? 'bg-yellow-900/40 text-yellow-300'
                          : 'bg-gray-700/60 text-gray-300'
                      }`}
                      title={[...row.gate.reasons, ...row.gate.flags].map((g) => g.label).join(', ')}
                    >
                      {row.gate.eligible === false ? '⚠ Watch' : '◆ Flag'}
                    </span>
                  ) : (
                    <span className="text-gray-700 text-[10px]">—</span>
                  )}
                </td>
                <td className="py-2 px-2">
                  <select
                    value={row.method.id}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => onAssign(row.ticker, e.target.value)}
                    className="bg-gray-800 border border-gray-700 rounded text-[11px] text-gray-200 px-1.5 py-0.5 max-w-[10rem]"
                    title={row.method.name}
                  >
                    {methods.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </td>
                <td className="py-2 px-3 text-right font-bold" style={{ color: scoreColor(row.result.value) }}>
                  {row.result.value == null ? '—' : Math.round(row.result.value)}
                </td>
                {CATEGORY_ORDER.map((c) => {
                  const v = catMap[c];
                  return (
                    <td key={c} className="py-2 px-3 text-right" style={{ color: scoreColor(v) }}>
                      {v == null ? '—' : Math.round(v)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

function CompanyScorePanel({ data }) {
  const results = data?.company_score_results || [];
  const methodology = data?.methodology;
  const templates = data?.method_templates || [];

  const scored = useMemo(
    () => results.filter((r) => !r.error && Array.isArray(r.signals)),
    [results],
  );
  const errored = useMemo(() => results.filter((r) => r.error), [results]);

  const defaults = useMemo(() => defaultWeightsFrom(data), [data]);
  const catalog = scored[0]?.signals || [];
  const allKeys = useMemo(() => catalog.map((s) => s.key), [catalog]);
  const zeroWeights = useMemo(
    () => Object.fromEntries(allKeys.map((k) => [k, 0])),
    [allKeys],
  );

  // Methods = built-in Default + the user's saved presets.
  const [presets, setPresets] = useState([]);
  const methods = useMemo(
    () => [
      { id: DEFAULT_METHOD_ID, name: 'Default', weights: defaults, builtin: true },
      ...presets.map((p) => ({
        id: String(p.id),
        name: p.name,
        weights: { ...zeroWeights, ...(p.weights || {}) },
        builtin: false,
      })),
    ],
    [presets, defaults, zeroWeights],
  );
  const methodById = useMemo(
    () => Object.fromEntries(methods.map((m) => [m.id, m])),
    [methods],
  );

  // Per-stock assignment: { TICKER: methodId }. Absent ⇒ Default.
  const [assignments, setAssignments] = useState({});
  const methodForTicker = useCallback(
    (t) => methodById[assignments[t]] || methodById[DEFAULT_METHOD_ID],
    [methodById, assignments],
  );

  const [selectedTicker, setSelectedTicker] = useState(null);
  const [showMethodology, setShowMethodology] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);

  // Editor working state.
  const [editorMethodId, setEditorMethodId] = useState('new'); // 'new' | 'default' | preset id
  const [editName, setEditName] = useState('');
  const [editWeights, setEditWeights] = useState(defaults);

  const loadPresets = useCallback(async () => {
    try {
      setPresets(await presetStore.listPresets());
    } catch (e) {
      setPresets([]);
    }
  }, []);
  const loadAssignments = useCallback(async () => {
    try {
      const { data: d } = await getCompanyScoreAssignments();
      const m = {};
      for (const [t, id] of Object.entries(d?.assignments || {})) m[t] = String(id);
      setAssignments(m);
    } catch (e) {
      setAssignments({});
    }
  }, []);
  useEffect(() => {
    loadPresets();
    loadAssignments();
  }, [loadPresets, loadAssignments]);

  // Live aggregation per ticker, each under its assigned method. Sorted desc.
  const rows = useMemo(
    () =>
      scored
        .map((r) => {
          const method = methodForTicker(r.ticker);
          const result = aggregate(r.signals, method.weights);
          return { ...r, method, result, gate: gateState(result, r.gates) };
        })
        // Eligible first, then by score desc — gated names sink to the bottom.
        .sort((a, b) => {
          const ae = a.gate.eligible ? 1 : 0;
          const be = b.gate.eligible ? 1 : 0;
          if (ae !== be) return be - ae;
          return (b.result.value ?? -1) - (a.result.value ?? -1);
        }),
    [scored, methodForTicker],
  );

  const selected = rows.find((r) => r.ticker === selectedTicker) || rows[0] || null;

  const handleAssign = async (ticker, methodId) => {
    setAssignments((prev) => {
      const next = { ...prev };
      if (methodId === DEFAULT_METHOD_ID) delete next[ticker];
      else next[ticker] = methodId;
      return next;
    });
    try {
      await saveCompanyScoreAssignment({
        ticker,
        preset_id: methodId === DEFAULT_METHOD_ID ? null : Number(methodId),
      });
    } catch (e) {
      setStatusMsg('Could not save assignment');
    }
  };

  // --- Editor handlers ---
  const selectEditorMethod = (id) => {
    setEditorMethodId(id);
    if (id === 'new') {
      setEditName('');
      setEditWeights({ ...defaults });
    } else {
      const m = methodById[id];
      setEditName(m?.builtin ? m.name : m?.name || '');
      setEditWeights({ ...zeroWeights, ...(m?.weights || defaults) });
    }
  };
  const loadTemplate = (t) => {
    // Land on a NEW, named, pre-weighted method — one "Save as new" persists it.
    setEditorMethodId('new');
    setEditName(t?.name || '');
    setEditWeights({ ...zeroWeights, ...(t?.weights || {}) });
    setStatusMsg(`Loaded template “${t?.name}” — Save as new to keep it`);
  };
  const openEditor = () => {
    setShowEditor((v) => {
      const next = !v;
      if (next) selectEditorMethod('new');
      return next;
    });
  };
  const setEditWeight = (key, value) =>
    setEditWeights((prev) => ({ ...prev, [key]: value }));

  const editorIsDefault = editorMethodId === DEFAULT_METHOD_ID;
  const editorIsExisting = editorMethodId !== 'new' && editorMethodId !== DEFAULT_METHOD_ID;

  const handleSaveMethod = async () => {
    const name = editName.trim();
    if (!name) {
      setStatusMsg('Enter a method name first');
      return;
    }
    const weights = Object.fromEntries(allKeys.map((k) => [k, Number(editWeights[k]) || 0]));
    if (!Object.values(weights).some((w) => w > 0)) {
      setStatusMsg('Include at least one feature');
      return;
    }
    try {
      if (editorIsExisting) {
        await presetStore.savePreset(Number(editorMethodId), { name, weights });
        setStatusMsg(`Updated “${name}”`);
      } else {
        const created = await presetStore.createPreset({ name, weights });
        setStatusMsg(`Created “${name}”`);
        if (created?.id) setEditorMethodId(String(created.id));
      }
      await loadPresets();
    } catch (e) {
      const msg = e?.response?.data?.name?.[0] || e?.response?.data?.weights?.[0] || 'Save failed';
      setStatusMsg(msg);
    }
  };

  const handleDeleteMethod = async () => {
    if (!editorIsExisting) return;
    try {
      await presetStore.removePreset(Number(editorMethodId));
      setStatusMsg('Method deleted');
      selectEditorMethod('new');
      await loadPresets();
      await loadAssignments(); // backend cascade cleared affected rows
    } catch (e) {
      setStatusMsg('Delete failed');
    }
  };

  if (!scored.length) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 text-sm text-gray-400">
        No company scores yet. Run an analysis from the Analysis Input tab.
        {errored.length > 0 && (
          <ul className="mt-2 text-xs text-yellow-400/80 space-y-1">
            {errored.map((e) => (
              <li key={e.ticker}>• {e.ticker}: {e.message}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gray-950/60 border border-gray-800 rounded-lg p-4 space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-base font-semibold text-white">Company Score</h2>
          <p className="text-xs text-gray-500">
            A 0–100 buy-attractiveness read per stock. Build named scoring methods (features +
            weights) and assign each stock the method that fits it.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowMethodology(true)}
            className="px-3 py-1.5 text-xs rounded-md border border-blue-500/60 text-blue-300 hover:bg-blue-600/20"
          >
            ⓘ How is this calculated?
          </button>
          <button
            onClick={openEditor}
            className="px-3 py-1.5 text-xs rounded-md border border-gray-600 text-gray-300 hover:bg-gray-700/40"
          >
            Scoring methods
          </button>
        </div>
      </div>

      <RankedTable
        rows={rows}
        methods={methods}
        selectedTicker={selected?.ticker}
        onSelect={setSelectedTicker}
        onAssign={handleAssign}
      />

      {/* Gauge card for the selected stock */}
      {selected && (
        <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
          <div className="flex items-center gap-4">
            <Gauge value={selected.result.value} />
            <div>
              <div className="text-sm text-gray-400">
                {selected.ticker} · <span className="text-gray-300">{selected.method.name}</span>
              </div>
              <div className="text-lg font-bold uppercase tracking-wide" style={{ color: scoreColor(selected.result.value) }}>
                {selected.result.label || '—'}
              </div>
              {selected.result.degraded && selected.result.value != null && (
                <div className="text-[11px] text-yellow-500/80 mt-1">
                  based on {Math.round(selected.result.coverage * 100)}% of intended signals
                </div>
              )}
              {(selected.gate?.reasons?.length > 0 || selected.gate?.flags?.length > 0) && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {selected.gate.reasons.map((g) => (
                    <span key={g.code} className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-900/40 text-yellow-300">
                      ⚠ {g.label}
                    </span>
                  ))}
                  {selected.gate.flags.map((g) => (
                    <span key={g.code} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-700/60 text-gray-300">
                      {g.label}
                    </span>
                  ))}
                </div>
              )}
              {selected.display?.next_earnings && (
                <div className="text-[11px] text-gray-500 mt-1">Next earnings {selected.display.next_earnings}</div>
              )}
              {selected.price != null && (
                <div className="text-[11px] text-gray-500 mt-1">Price ${Number(selected.price).toFixed(2)}</div>
              )}
            </div>
          </div>
          <CategoryBreakdown
            result={selected.result}
            meta={methodology?.signals}
            fairValues={selected.fair_values}
            price={selected.price}
          />
        </div>
      )}

      {errored.length > 0 && (
        <div className="bg-yellow-900/20 border border-yellow-700 rounded-md p-3 text-yellow-200 text-xs">
          <strong>Couldn't score:</strong>{' '}
          {errored.map((e) => `${e.ticker} (${e.message})`).join('; ')}
        </div>
      )}

      <CompanyScoreMethodEditorModal
        isOpen={showEditor}
        onClose={() => setShowEditor(false)}
        methods={methods}
        templates={templates}
        editorMethodId={editorMethodId}
        editName={editName}
        editWeights={editWeights}
        scored={scored}
        catalog={catalog}
        editorIsDefault={editorIsDefault}
        editorIsExisting={editorIsExisting}
        statusMsg={statusMsg}
        onSelectMethod={selectEditorMethod}
        onLoadTemplate={loadTemplate}
        onChangeName={setEditName}
        onChangeWeight={setEditWeight}
        onSave={handleSaveMethod}
        onDelete={handleDeleteMethod}
      />

      <CompanyScoreMethodologyModal
        isOpen={showMethodology}
        onClose={() => setShowMethodology(false)}
        title={selected ? `${selected.ticker} · ${selected.method.name}` : ''}
        result={selected?.result}
        methodology={methodology}
      />
    </div>
  );
}

export default CompanyScorePanel;
